import React, { createContext, useContext, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue>({ showToast: () => undefined });

const iconMap: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle2 size={16} color="var(--succ)" />,
  error:   <XCircle size={16} color="var(--err)" />,
  warning: <AlertTriangle size={16} color="#F59E0B" />,
  info:    <Info size={16} color="#3B82F6" />,
};

let nextId = 0;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = nextId++;
    setToasts((prev) => [...prev, { id, message, type }]);
    if (type === 'success' || type === 'info') {
      setTimeout(() => dismiss(id), 5000);
    }
  }, [dismiss]);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          zIndex: 2000,
          width: 'min(380px, calc(100vw - 48px))',
        }}>
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`toast toast-${t.type}`}
              style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}
            >
              <span style={{ flexShrink: 0, marginTop: '1px' }}>{iconMap[t.type]}</span>
              <span style={{ flex: 1, fontSize: '13.5px', color: 'var(--text-1)', lineHeight: 1.5 }}>
                {t.message}
              </span>
              <button
                onClick={() => dismiss(t.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', padding: '0', flexShrink: 0 }}
                aria-label="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);
